/**
 * Replay buffer for clips: two MediaRecorders over the program, started `seconds` apart and each
 * restarted every 2 x `seconds`, so that once the buffer has run for `seconds` one of them always
 * holds between `seconds` and 2 x `seconds` of program from its own start (a complete, playable file).
 * Saving snapshots that older recorder; a clip therefore covers between `seconds` and 2 x `seconds`.
 */
import type { Logger, Unsubscribe } from '../core/services';
import type { ReplayBufferStartOptions, ReplayBufferState, ReplayClip } from './api';
import { listSupportedFormats } from './formats';

const TIMESLICE_MS = 1000;
/** A recorder holding more than this is discarded and restarted. */
export const MAX_BUFFER_BYTES = 256 * 1024 * 1024;
/** How long save() waits for the chunk requested from the recorder before using what it has. */
const FLUSH_TIMEOUT_MS = 2000;

/** The part of MediaRecorder the buffer uses. */
export interface MediaRecorderLike extends EventTarget {
  readonly state: 'inactive' | 'recording' | 'paused';
  readonly mimeType: string;
  start(timeslice?: number): void;
  stop(): void;
  requestData(): void;
}

export type CreateMediaRecorder = (stream: MediaStream, options: MediaRecorderOptions) => MediaRecorderLike;

export interface ReplayBuffer {
  /** Throws (and enters 'error') when the recorders cannot be created. */
  start(options: ReplayBufferStartOptions): void;
  /**
   * The older recorder's program so far, without interrupting it. Rejects when not running or
   * when nothing has been buffered yet. `fileName` is only used for logging.
   */
  save(fileName: string): Promise<ReplayClip>;
  stop(): void;
  getState(): ReplayBufferState;
  subscribe(listener: (state: ReplayBufferState) => void): Unsubscribe;
}

interface Slot {
  name: 'A' | 'B';
  recorder: MediaRecorderLike | null;
  /** Listener cleanup for the current recorder. */
  detach: () => void;
  chunks: Blob[];
  bytes: number;
  startedAt: number;
  timer: ReturnType<typeof setTimeout> | null;
  /** save() calls waiting for the chunk they requested. */
  waiters: Array<() => void>;
}

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function createReplayBuffer(deps: {
  logger: Logger;
  createMediaRecorder?: CreateMediaRecorder;
  now?: () => number;
}): ReplayBuffer {
  const log = (level: 'info' | 'warn' | 'error', message: string) => deps.logger.log(level, 'replay', message);
  const now = deps.now ?? (() => Date.now());
  const create: CreateMediaRecorder = deps.createMediaRecorder ?? ((stream, options) => new MediaRecorder(stream, options));
  const listeners = new Set<(state: ReplayBufferState) => void>();

  let state: ReplayBufferState = { status: 'idle', seconds: 0, error: null };
  let options: ReplayBufferStartOptions | null = null;
  let mimeType = '';
  let slots: Slot[] = [];
  let firstRestart: ReturnType<typeof setTimeout> | null = null;

  const emit = (next: ReplayBufferState): void => {
    state = next;
    for (const l of [...listeners]) {
      try {
        l(state);
      } catch (err) {
        log('error', `Replay buffer listener threw: ${messageOf(err)}`);
      }
    }
  };

  const flushWaiters = (slot: Slot): void => {
    for (const w of slot.waiters.splice(0)) w();
  };

  const retire = (slot: Slot): void => {
    if (slot.timer) clearTimeout(slot.timer);
    slot.timer = null;
    flushWaiters(slot);
    slot.detach();
    const rec = slot.recorder;
    slot.recorder = null;
    if (rec && rec.state !== 'inactive') {
      try {
        rec.stop();
      } catch {
        // Already stopped by the browser.
      }
    }
    slot.chunks = [];
    slot.bytes = 0;
  };

  function fail(message: string): void {
    log('error', message);
    teardown();
    emit({ status: 'error', seconds: state.seconds, error: message });
  }

  /** (Re)starts one slot with a fresh recorder; its previous chunks are discarded. */
  function launch(slot: Slot): void {
    const opts = options;
    if (!opts) return;
    retire(slot);
    const rec = create(opts.stream, {
      ...(mimeType ? { mimeType } : {}),
      videoBitsPerSecond: Math.round(opts.videoKbps * 1000),
      audioBitsPerSecond: Math.round(opts.audioKbps * 1000),
    });
    const onData = (e: Event): void => {
      const data = (e as BlobEvent).data;
      if (data && data.size > 0) {
        slot.chunks.push(data);
        slot.bytes += data.size;
      }
      if (slot.bytes > MAX_BUFFER_BYTES) {
        log('warn', `Replay recorder ${slot.name} exceeded ${MAX_BUFFER_BYTES / 1048576} MiB; its buffer was dropped`);
        launch(slot);
        return;
      }
      flushWaiters(slot);
    };
    const onError = (e: Event): void => {
      const err = (e as Event & { error?: DOMException }).error;
      fail(`Replay buffer recorder error: ${err?.message || err?.name || 'unknown'}`);
    };
    rec.addEventListener('dataavailable', onData);
    rec.addEventListener('error', onError);
    slot.detach = () => {
      rec.removeEventListener('dataavailable', onData);
      rec.removeEventListener('error', onError);
      slot.detach = () => undefined;
    };
    slot.recorder = rec;
    slot.startedAt = now();
    rec.start(TIMESLICE_MS);
    slot.timer = setTimeout(() => restart(slot), opts.seconds * 2000);
  }

  function restart(slot: Slot): void {
    try {
      launch(slot);
    } catch (err) {
      fail(`Replay buffer could not restart: ${messageOf(err)}`);
    }
  }

  function teardown(): void {
    if (firstRestart) clearTimeout(firstRestart);
    firstRestart = null;
    for (const slot of slots) retire(slot);
    slots = [];
    options = null;
  }

  const newSlot = (name: Slot['name']): Slot => ({
    name, recorder: null, detach: () => undefined, chunks: [], bytes: 0, startedAt: 0, timer: null, waiters: [],
  });

  return {
    start(opts) {
      teardown();
      if (!(opts.seconds > 0)) throw new Error('The replay buffer needs a positive length');
      options = opts;
      mimeType = opts.mimeType === 'auto' ? (listSupportedFormats()[0]?.mimeType ?? '') : opts.mimeType;
      const a = newSlot('A');
      const b = newSlot('B');
      slots = [a, b];
      try {
        launch(a);
      } catch (err) {
        const message = `Replay buffer could not start: ${messageOf(err)}`;
        fail(message);
        throw new Error(message);
      }
      firstRestart = setTimeout(() => {
        firstRestart = null;
        restart(b);
      }, opts.seconds * 1000);
      emit({ status: 'running', seconds: opts.seconds, error: null });
      log('info', `Replay buffer started (${opts.seconds} s, ${a.recorder?.mimeType || mimeType || 'default format'})`);
    },

    async save(fileName) {
      if (state.status !== 'running') throw new Error('The replay buffer is not running');
      const older = slots
        .filter((s) => s.recorder && s.recorder.state !== 'inactive')
        .sort((x, y) => x.startedAt - y.startedAt)[0];
      if (!older?.recorder) throw new Error('The replay buffer is not running');
      const rec = older.recorder;
      const startedAt = older.startedAt;
      // Ask for what the recorder holds right now; a restart in the meantime also releases the wait.
      await new Promise<void>((resolve) => {
        const timer = setTimeout(done, FLUSH_TIMEOUT_MS);
        function done(): void {
          clearTimeout(timer);
          const i = older!.waiters.indexOf(done);
          if (i >= 0) older!.waiters.splice(i, 1);
          resolve();
        }
        older.waiters.push(done);
        try {
          rec.requestData();
        } catch {
          done();
        }
      });
      // The slot may have been restarted while waiting; only its own recorder's chunks belong to this clip.
      const chunks = older.recorder === rec ? [...older.chunks] : [];
      if (chunks.length === 0) throw new Error('Nothing has been buffered yet');
      const blob = new Blob(chunks, { type: rec.mimeType || mimeType || 'video/webm' });
      const durationMs = Math.max(0, now() - startedAt);
      log('info', `Clip "${fileName}" taken from replay recorder ${older.name} (${Math.round(durationMs / 1000)} s, ${blob.size} bytes)`);
      return { blob, durationMs };
    },

    stop() {
      const wasActive = state.status !== 'idle';
      teardown();
      if (wasActive) {
        emit({ status: 'idle', seconds: state.seconds, error: null });
        log('info', 'Replay buffer stopped');
      }
    },

    getState: () => state,

    subscribe(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
  };
}
